/**
 * RateSummaryCard — the "(a) rate summary" section of the one-page public
 * Confirm & book flow. Pure presentational: renders the selected card
 * (state.route.final_search_form) + lane (queryParams) the same way the rate
 * card showed them, so what the customer confirms is what they clicked.
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { isEmpty } from '../../../util/isEmpty';
import { color, radius } from '../../../styles/designTokens';
import { SectionCard, SectionHeading } from './confirmTheme';

const Lane = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${color.navy};
  line-height: 1.4;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 6px 14px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: 12.5px;
  color: ${color.ink2};
`;

const PriceBlock = styled.div`
  text-align: right;
  min-width: 130px;
  @media (max-width: 480px) {
    text-align: left;
  }
`;

const Price = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${color.navy};
  font-variant-numeric: tabular-nums;
`;

const PriceCaption = styled.div`
  font-size: 11px;
  color: ${color.ink2};
`;

const DiscountTag = styled.span`
  display: inline-block;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${color.ok};
  background: ${color.okSoft};
  border: 1px solid ${color.okLine};
  border-radius: ${radius.base};
  padding: 1px 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const CONTAINER_LABELS = Object.freeze([
  ['_20stCounter', "20' Standard"],
  ['_40stCounter', "40' Standard"],
  ['_40hqCounter', "40' High Cube"],
  ['_45hqCounter', "45' High Cube"],
]);

const containerSummary = (container) => {
  const parts = CONTAINER_LABELS.filter(([key]) => Number((container || {})[key]) > 0)
    .map(([key, label]) => `${Number(container[key])} × ${label}`);
  return parts.join(' · ');
};

const MODE_LABELS = Object.freeze({
  'api-fcl': 'Ocean FCL',
  'api-lcl': 'Ocean LCL',
  'api-air': 'Air',
});

const formatEtd = (etd) => {
  if (!etd) return null;
  const parsed = moment(etd, [moment.ISO_8601, 'YYYY-MM-DD'], true);
  const value = parsed.isValid() ? parsed : moment(new Date(etd));
  return value.isValid() ? value.format('MMM D, YYYY') : null;
};

const formatMoney = (value) => {
  const n = Number(value);
  if (isNaN(n)) return null;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const RateSummaryCard = ({ finalSearchForm, queryParams, discount }) => {
  const form = finalSearchForm || {};
  const card = form.selected_card_json || {};
  const params = queryParams || {};

  const fromName = (params.from && params.from.name) || '—';
  const toName = (params.to && params.to.name) || '—';
  const modeLabel = MODE_LABELS[form.type] || MODE_LABELS[params.type] || '';
  const carrierName =
    card.sealine || (card.carrier && card.carrier.company_name) || null;
  const etd = formatEtd(card.etd_origin);
  const containers =
    form.type === 'api-fcl' ? containerSummary(form.container) : null;
  const price = formatMoney(card.bookNowPrice);
  const hasDiscount = !isEmpty(discount) && discount.id;

  return (
    <SectionCard aria-label="Your selected rate">
      <SectionHeading style={{ marginBottom: 12 }}>Your shipment</SectionHeading>
      <Row>
        <div style={{ flex: '1 1 260px' }}>
          <Lane>
            {fromName} → {toName}
          </Lane>
          <MetaRow>
            {modeLabel && <span>{modeLabel}</span>}
            {carrierName && <span>{carrierName}</span>}
            {etd && <span>ETD {etd}</span>}
            {containers && <span>{containers}</span>}
          </MetaRow>
        </div>
        {price && (
          <PriceBlock>
            <Price>{price}</Price>
            <PriceCaption>USD · all-in freight</PriceCaption>
            {hasDiscount && Number(discount.percent) > 0 && (
              <DiscountTag>{Number(discount.percent)}% off applied</DiscountTag>
            )}
          </PriceBlock>
        )}
      </Row>
    </SectionCard>
  );
};

RateSummaryCard.propTypes = {
  finalSearchForm: PropTypes.object,
  queryParams: PropTypes.object,
  discount: PropTypes.object,
};

export default RateSummaryCard;
